/**
 * Keeps main's copy of the UI context current for the hang events
 * (`app:renderer-unresponsive` / `-recovered`; see uiContextPublisher.ts).
 *
 * Mounted once, in App. It subscribes to the stores directly rather than
 * selecting from them, so publishing never re-renders App; only
 * `restoreInProgress` comes in as an argument, because the restore flow's
 * state lives in App itself.
 */

import { useEffect, useLayoutEffect, useRef } from 'react';
import { useWorkspaceStore } from '../../features/workspace';
import { useConnectionStore } from '../../features/connections/store/connectionStore';
import { buildUiContextSnapshot, createUiContextPublisher } from './uiContextPublisher';

export function useHangContextPublisher(restoreInProgress: boolean): void {
  const restoreInProgressRef = useRef(restoreInProgress);
  restoreInProgressRef.current = restoreInProgress;
  const publishCurrentRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    const setUiContext = window.electronAPI?.analytics?.setUiContext;
    if (!setUiContext) return undefined;
    const publisher = createUiContextPublisher((snapshot) => {
      try {
        setUiContext(snapshot);
      } catch {
        // Telemetry must never affect the UI.
      }
    });
    const publishCurrent = (): void => {
      const { tabs, activeTabId } = useWorkspaceStore.getState();
      const { activeConnections, connections } = useConnectionStore.getState();
      publisher.publish(
        buildUiContextSnapshot({
          tabs,
          activeTabId,
          activeConnections,
          connections,
          restoreInProgress: restoreInProgressRef.current,
        }),
      );
    };
    publishCurrentRef.current = publishCurrent;
    publishCurrent();
    const unsubscribeWorkspace = useWorkspaceStore.subscribe(publishCurrent);
    const unsubscribeConnections = useConnectionStore.subscribe(publishCurrent);
    return () => {
      unsubscribeWorkspace();
      unsubscribeConnections();
      publisher.dispose();
      publishCurrentRef.current = null;
    };
  }, []);

  // Layout, not passive: it runs in the same commit that flips the flag,
  // before paint, so a restore that starts a long synchronous stretch has
  // already told main (the publisher sends the rising edge unthrottled).
  useLayoutEffect(() => {
    publishCurrentRef.current?.();
  }, [restoreInProgress]);
}
